import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Field, Form, Formik } from 'formik';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import FiltersCard from '../../modules/layout/components/FiltersCard';
import { FormikDatePicker } from '../../modules/layout/components/FormikDatePicker';
import Loader from '../../modules/layout/components/Loader';
import Pagination from '../../modules/layout/components/Pagination';
import SortDirection from '../../modules/layout/components/SortDirection';
import { useToast } from '../../modules/layout/components/ToastContext';
import { searchTransactions } from './transactionExplorerService';
import { ITransactionSearchFilters, ITransactionSummary, TransactionStatus } from './types';
import { formatDateTime, toApiDateTime } from '../../helpers/DateTimeHelper';

interface FilterFormState {
  dateFrom: string;
  dateTo: string;
  className: string;
  status: string; // '' | '1' | '2'
  username: string;
}

const initialFormState: FilterFormState = {
  dateFrom: '',
  dateTo: '',
  className: '',
  status: '',
  username: '',
};

const TransactionExplorerList = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [rows, setRows] = useState<ITransactionSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState('lastExecutedAt');
  const [asc, setAsc] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterFormState>(initialFormState);

  const order = asc ? 'Asc' : 'Desc';

  const buildFilters = (f: FilterFormState): ITransactionSearchFilters => ({
    dateFrom: toApiDateTime(f.dateFrom),
    dateTo: toApiDateTime(f.dateTo, true),
    className: f.className || undefined,
    status: f.status ? (Number(f.status) as TransactionStatus) : undefined,
    username: f.username || undefined,
    page,
    pageSize,
    sortBy: sortField,
    order,
  });

  const fetchTransactions = async (filters: FilterFormState = activeFilters) => {
    setLoading(true);
    try {
      const result = await searchTransactions(buildFilters(filters));
      setRows(result.items ?? []);
      setTotal(result.totalCount ?? 0);
    } catch (error) {
      showToast(t('ConnectionError', { error: String(error) }), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortField, asc]);

  const handleFilterSearch = async (filters: FilterFormState) => {
    setPage(1);
    setActiveFilters(filters);
    await fetchTransactions(filters);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setAsc(!asc);
    } else {
      setSortField(field);
      setAsc(true);
    }
  };

  return (
    <div className="gg-transactionExplorerList container mt-4">
      <AppBreadcrumb items={[{ title: t('Home'), href: '/' }, { title: t('TransactionExplorer') }]} />

      <h3 className="mb-3">{t('TransactionExplorer')}</h3>

      <FiltersCard>
        <Formik initialValues={activeFilters} onSubmit={handleFilterSearch} enableReinitialize>
          {({ resetForm }) => (
            <Form>
              <div className="row">
                <div className="col-md-6 col-lg-4 col-xl-3">
                  <label className="form-label w-100">
                    {t('ClassName')}
                    <Field className="form-control" name="className" />
                  </label>
                </div>
                <div className="col-md-6 col-lg-4 col-xl-3">
                  <label className="form-label w-100">
                    {t('Status')}
                    <Field as="select" className="form-select" name="status">
                      <option value="">{t('All')}</option>
                      <option value={String(TransactionStatus.Success)}>{t('Success')}</option>
                      <option value={String(TransactionStatus.Failed)}>{t('Failed')}</option>
                    </Field>
                  </label>
                </div>
                <div className="col-md-6 col-lg-4 col-xl-3">
                  <label className="form-label w-100">
                    {t('Caller')}
                    <Field className="form-control" name="username" />
                  </label>
                </div>
                <div className="col-md-6 col-lg-4 col-xl-3">
                  <label className="form-label w-100">
                    {t('DateFrom')}
                    <FormikDatePicker name="dateFrom" showTime placeholderText={t('ChooseDate')} />
                  </label>
                </div>
                <div className="col-md-6 col-lg-4 col-xl-3">
                  <label className="form-label w-100">
                    {t('DateTo')}
                    <FormikDatePicker name="dateTo" showTime endOfDayDefault placeholderText={t('ChooseDate')} />
                  </label>
                </div>
                <div className="col-md-6 col-lg-4 col-xl-3 align-self-end">
                  <button type="submit" className="btn gg-btn gg-btn-submit mb-2 me-2">
                    {t('Search')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary text-white mb-2"
                    onClick={() => {
                      resetForm();
                      setActiveFilters(initialFormState);
                      handleFilterSearch(initialFormState);
                    }}
                  >
                    {t('Reset')}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </FiltersCard>

      {loading && <Loader />}

      <div className="table-responsive border p-1 rounded-2">
        <table className="table table-sm table-striped-columns small">
          <thead>
            <tr>
              <th role="button" className="text-nowrap" onClick={() => handleSort('className')}>
                {t('Transaction')}
                <SortDirection isActive={sortField === 'className'} order={order.toLowerCase()} />
              </th>
              <th className="text-nowrap">{t('Endpoint')}</th>
              <th role="button" className="text-nowrap text-end" onClick={() => handleSort('totalCalls')}>
                {t('TotalCalls')}
                <SortDirection isActive={sortField === 'totalCalls'} order={order.toLowerCase()} />
              </th>
              <th role="button" className="text-nowrap text-end" onClick={() => handleSort('successful')}>
                {t('Successful')}
                <SortDirection isActive={sortField === 'successful'} order={order.toLowerCase()} />
              </th>
              <th role="button" className="text-nowrap text-end" onClick={() => handleSort('failed')}>
                {t('Failed')}
                <SortDirection isActive={sortField === 'failed'} order={order.toLowerCase()} />
              </th>
              <th role="button" className="text-nowrap text-end" onClick={() => handleSort('successRate')}>
                {t('SuccessRate')}
                <SortDirection isActive={sortField === 'successRate'} order={order.toLowerCase()} />
              </th>
              <th role="button" className="text-nowrap" onClick={() => handleSort('lastExecutedAt')}>
                {t('LastExecuted')}
                <SortDirection isActive={sortField === 'lastExecutedAt'} order={order.toLowerCase()} />
              </th>
            </tr>
          </thead>
          <tbody className="table-group-divider">
            {rows.map((row) => (
              <tr
                key={row.className}
                role="button"
                onClick={() => navigate(`/transaction-explorer/${encodeURIComponent(row.className)}`)}
              >
                <td className="text-nowrap">
                  <span className="text-decoration-underline text-primary">
                    {row.routeLabel || row.className}
                  </span>
                  {row.routeLabel && <div className="text-muted small">{row.className}</div>}
                </td>
                <td className="text-nowrap">{row.postLocation}</td>
                <td className="text-end">{row.totalCalls}</td>
                <td className="text-end text-success">{row.successful}</td>
                <td className="text-end text-danger">{row.failed}</td>
                <td className="text-end">{(row.successRate * 100).toFixed(1)}%</td>
                <td className="text-nowrap">
                  {row.lastExecutedAt
                    ? formatDateTime(row.lastExecutedAt, false)
                    : ''}
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-muted py-3">
                  {t('NoResults')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={page}
        onPageChange={(p) => setPage(p)}
        onPageSizeChange={(size) => setPageSize(size)}
        totalItems={total}
        pageSize={pageSize}
      />
    </div>
  );
};

export default TransactionExplorerList;
